const WEBHOOK_URL = import.meta.env.VITE_SLACK_ERROR_WEBHOOK_URL;

// Evita saturar el canal si un error se repite en loop (p. ej. un render roto).
const MAX_ALERTS_PER_SESSION = 15;
const COOLDOWN_MS = 30000;

let sentCount = 0;
const lastSentAt = new Map();

// Único identificador de "sujeto" disponible en el sitio público: el admin logueado, si lo hay.
function getSubjectLabel() {
  try {
    return localStorage.getItem('admin_nombre') || null;
  } catch {
    return null;
  }
}

// Extrae "archivo:línea:columna" del primer frame de un stack trace,
// para los casos (p. ej. unhandledrejection) donde el navegador no da esos datos por separado.
function parseLocationFromStack(stack) {
  if (!stack) return null;
  const match = String(stack).match(/(?:https?:\/\/[^\s)]+?|[\w./-]+\.jsx?):(\d+):(\d+)/);
  if (!match) return null;
  return { file: match[0].split(':').slice(0, -2).join(':'), line: match[1], col: match[2] };
}

// Convierte un argumento de console.error a texto legible. Los Event del DOM (p. ej.
// img.onerror) no tienen mensaje propio: 'String(event)' da solo "[object Event]".
function stringifyConsoleArg(a) {
  if (a instanceof Error) return a.stack || a.message;
  if (a instanceof Event) {
    const target = a.target;
    const targetInfo = target?.tagName
      ? ` target=<${target.tagName.toLowerCase()}${target.src ? ` src="${target.src}"` : ''}>`
      : '';
    return `[Event type="${a.type}"${targetInfo}]`;
  }
  return String(a);
}

function shortFileName(url) {
  if (!url) return null;
  try {
    return url.split('/').pop();
  } catch {
    return url;
  }
}

// Slack no permite CORS en su endpoint de webhooks: 'no-cors' evita el preflight
// que lo bloquearía, a costa de no poder leer la respuesta (fire-and-forget).
function sendToSlack(payload) {
  if (!WEBHOOK_URL) return;
  try {
    fetch(WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    // El reporte de errores nunca debe romper la app.
  }
}

function buildPayload({
  errorType,
  message,
  file,
  line,
  col,
  componentStack,
  stack,
  headerEmoji = '🚨',
  headerText = 'Error en LABPSI',
  color = '#e01e5a',
}) {
  const subject = getSubjectLabel();

  const fields = [
    { label: 'Tipo', value: errorType },
    { label: 'Página', value: window.location.pathname },
    subject ? { label: 'Admin', value: subject } : null,
    file ? { label: 'Archivo:línea:col', value: `${file}:${line ?? '?'}:${col ?? '?'}` } : null,
  ].filter(Boolean);

  const blocks = [
    { type: 'header', text: { type: 'plain_text', text: `${headerEmoji} ${headerText}`, emoji: true } },
    { type: 'section', text: { type: 'mrkdwn', text: `*${message}*` } },
    {
      type: 'section',
      fields: fields.map((f) => ({ type: 'mrkdwn', text: `*${f.label}:*\n${f.value}` })),
    },
  ];

  if (componentStack) {
    blocks.push({
      type: 'section',
      text: { type: 'mrkdwn', text: `*Árbol de componentes:*\n\`\`\`${componentStack.trim().slice(0, 800)}\`\`\`` },
    });
  }

  if (stack) {
    blocks.push({
      type: 'section',
      text: { type: 'mrkdwn', text: `*Stack trace:*\n\`\`\`${String(stack).slice(0, 1000)}\`\`\`` },
    });
  }

  blocks.push({
    type: 'context',
    elements: [{ type: 'mrkdwn', text: `🕐 ${new Date().toLocaleString('es-AR')}` }],
  });

  // 'attachments' con color agrega la franja de color al costado (Block Kit por sí solo no tiene color lateral).
  return {
    text: `${headerText}: ${message}`, // fallback para notificaciones
    attachments: [{ color, blocks }],
  };
}

function shouldSend(dedupeKey) {
  if (!WEBHOOK_URL || sentCount >= MAX_ALERTS_PER_SESSION) return false;
  const now = Date.now();
  const last = lastSentAt.get(dedupeKey);
  if (last && now - last < COOLDOWN_MS) return false;
  lastSentAt.set(dedupeKey, now);
  sentCount += 1;
  return true;
}

// Extensiones del navegador (MetaMask, Stacks, etc.) y errores cross-origin ofuscados
// ("Script error.") no son de LABPSI: saturan Slack sin valor de diagnóstico.
function isExtensionNoise({ message, file, stack } = {}) {
  const blob = [message, file, stack].filter(Boolean).join('\n');
  if (!blob) return false;
  if (/(?:chrome|moz|safari|edge|webkit)-extension:\/\//i.test(blob)) return true;
  if (/^Script error\.?$/i.test(String(message || '').trim())) return true;
  if (/Failed to connect to MetaMask/i.test(blob)) return true;
  if (/Cannot redefine property:\s*StacksProvider/i.test(blob)) return true;
  if (/\bMetaMask\b/i.test(blob) && /inpage\.js/i.test(blob)) return true;
  return false;
}

// Errores "planos": window.onerror y unhandledrejection. No hay info de componente de React
// disponible acá (solo aplica a errores producidos durante el render, ver reportReactRenderError).
function reportRuntimeError({ errorType, message, file, line, col, stack }) {
  if (isExtensionNoise({ message, file, stack })) return;
  const loc = file ? { file: shortFileName(file), line, col } : parseLocationFromStack(stack);
  const dedupeKey = `${errorType}:${message}`.slice(0, 200);
  if (!shouldSend(dedupeKey)) return;

  sendToSlack(
    buildPayload({
      errorType,
      message,
      file: loc?.file,
      line: loc?.line,
      col: loc?.col,
      stack,
    })
  );
}

// Errores de render capturados por un ErrorBoundary de React: sí incluyen el árbol de componentes.
export function reportReactRenderError(error, componentStack) {
  if (isExtensionNoise({ message: error?.message, stack: error?.stack })) return;
  const dedupeKey = `React:${error?.message}`.slice(0, 200);
  if (!shouldSend(dedupeKey)) return;

  sendToSlack(
    buildPayload({
      errorType: error?.name || 'Error de render (React)',
      message: error?.message || String(error),
      componentStack,
      stack: error?.stack,
    })
  );
}

// Aviso de diagnóstico proactivo: para condiciones sospechosas detectadas en código
// que no llegan a lanzar una excepción (p. ej. una posición calculada fuera de rango).
export function reportDiagnosticWarning(message, extra) {
  const dedupeKey = `warn:${message}`.slice(0, 200);
  if (!shouldSend(dedupeKey)) return;

  sendToSlack(
    buildPayload({
      errorType: 'Diagnóstico',
      message,
      stack: extra,
      headerEmoji: '⚠️',
      headerText: 'Aviso de diagnóstico',
      color: '#f59e0b',
    })
  );
}

// Muchos warnings de React (loops de actualización, props inválidas, etc.) se emiten con
// console.error puro y nunca se lanzan como excepción — no los agarra ni window.onerror
// ni un ErrorBoundary. Se intercepta console.error para no perderlos.
let isReportingConsoleError = false;
function installConsoleErrorReporter() {
  const originalError = console.error;
  console.error = (...args) => {
    originalError.apply(console, args);
    if (isReportingConsoleError) return; // evita recursión si el reporte mismo logueara un error
    isReportingConsoleError = true;
    try {
      const message = args.map(stringifyConsoleArg).join(' ').slice(0, 500);
      if (isExtensionNoise({ message })) return;
      const dedupeKey = `console.error:${message.slice(0, 200)}`;
      if (shouldSend(dedupeKey)) {
        sendToSlack(
          buildPayload({
            errorType: 'console.error',
            message,
            headerEmoji: '⚠️',
            headerText: 'Warning en consola',
            color: '#f59e0b',
          })
        );
      }
    } finally {
      isReportingConsoleError = false;
    }
  };
}

export function initSlackErrorReporter() {
  if (!WEBHOOK_URL) {
    console.warn('[slackErrorReporter] VITE_SLACK_ERROR_WEBHOOK_URL no configurada, aviso a Slack deshabilitado.');
    return;
  }

  window.addEventListener('error', (event) => {
    reportRuntimeError({
      errorType: event.error?.name || 'Error',
      message: event.message || 'Error desconocido',
      file: event.filename,
      line: event.lineno,
      col: event.colno,
      stack: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportRuntimeError({
      errorType: 'Promise rechazada',
      message: reason?.message || String(reason),
      stack: reason?.stack,
    });
  });

  installConsoleErrorReporter();
}
